import type { NodeType } from '../../../core/entities/node';
import type { WorkspaceGraphMode } from '../../../core/ports/preferences-repository';
import type { OutlineSortKey } from '../../../core/policies/workspace/derive-outline-rows';
import { useLocale } from '../../preferences/i18n/LocaleProvider';

export type { OutlineSortKey } from '../../../core/policies/workspace/derive-outline-rows';

export function OutlineWorkspaceToolbar({
	currentGraphMode,
	localVisibleTypes,
	onCreate,
	onReturnToGraph,
	onSearchQueryChange,
	onSortDirectionChange,
	onSortKeyChange,
	onToggleGroupByType,
	onToggleVisibleType,
	readonly,
	createDisabled = false,
	createDisabledReason = '',
	searchQuery,
	sortDirection,
	sortKey,
	visibleCount,
	groupByType,
	totalCount,
}: {
	currentGraphMode: WorkspaceGraphMode;
	localVisibleTypes: NodeType[];
	onCreate: (type: NodeType) => void;
	onReturnToGraph: () => void;
	onSearchQueryChange: (query: string) => void;
	onSortDirectionChange: (direction: 'asc' | 'desc') => void;
	onSortKeyChange: (key: OutlineSortKey) => void;
	onToggleGroupByType: () => void;
	onToggleVisibleType: (type: NodeType) => void;
	readonly: boolean;
	createDisabled?: boolean;
	createDisabledReason?: string;
	searchQuery: string;
	sortDirection: 'asc' | 'desc';
	sortKey: OutlineSortKey;
	visibleCount: number;
	groupByType: boolean;
	totalCount: number;
}) {
	const { locale, t } = useLocale();
	const types: NodeType[] = ['Human', 'Project', 'Occupation'];

	return (
		<header className="outline-toolbar">
			<div className="outline-toolbar__top">
				<div className="workspace-title-group">
					<p className="mode-switcher__eyebrow">{t('workspace.mode.outline')}</p>
					<h2>{t('workspace.outline.title')}</h2>
					<p className="workspace-subtitle">{formatCount(locale, visibleCount, totalCount, t('workspace.outline.count'))}</p>
				</div>
				<div className="outline-toolbar__actions">
					<button className="hud-subtle-button" onClick={onReturnToGraph} type="button">
						{formatReturnLabel(currentGraphMode, t)}
					</button>
					{types.map((type) => (
						<button
							className="hud-chip"
							disabled={readonly || createDisabled}
							key={type}
							onClick={() => onCreate(type)}
							type="button"
						>
							{formatCreateLabel(locale, t('create'), type, t)}
						</button>
					))}
				</div>
			</div>
			<div className="outline-toolbar__controls">
				<label className="field-stack">
					<span className="field-label">{t('search')}</span>
					<input
						className="workspace-input"
						onChange={(event) => onSearchQueryChange(event.target.value)}
						placeholder={t('workspace.outline.searchPlaceholder')}
						value={searchQuery}
					/>
				</label>
				<label className="field-stack">
					<span className="field-label">{t('workspace.outline.sort')}</span>
					<select className="workspace-input" onChange={(event) => onSortKeyChange(event.target.value as OutlineSortKey)} value={sortKey}>
						<option value="displayName">{t('displayName')}</option>
						<option value="type">{t('workspace.outline.type')}</option>
						<option value="updatedAt">{t('updatedAt')}</option>
						<option value="connectionCount">{t('workspace.outline.connections')}</option>
					</select>
				</label>
				<label className="field-stack">
					<span className="field-label">{t('workspace.outline.sortDirection')}</span>
					<select className="workspace-input" onChange={(event) => onSortDirectionChange(event.target.value as 'asc' | 'desc')} value={sortDirection}>
						<option value="asc">{t('workspace.outline.sortAsc')}</option>
						<option value="desc">{t('workspace.outline.sortDesc')}</option>
					</select>
				</label>
			</div>
			<div className="outline-toolbar__bottom">
				<div className="hud-toolbar" role="group" aria-label={t('workspace.outline.filter')}>
					{types.map((type) => (
						<button
							aria-pressed={localVisibleTypes.includes(type)}
							className="hud-chip"
							key={type}
							onClick={() => onToggleVisibleType(type)}
							type="button"
						>
							{nodeTypeLabel(type, t)}
						</button>
					))}
				</div>
				<button aria-pressed={groupByType} className="hud-chip" onClick={onToggleGroupByType} type="button">
					{groupByType ? t('workspace.outline.groupByType') : t('workspace.outline.ungrouped')}
				</button>
			</div>
			<div className="outline-toolbar__hints">
				{createDisabledReason && (
					<p className="hud-hint" role="note">
						{createDisabledReason}
					</p>
				)}
				<p className="hud-hint" id="outline-workflow-hint" role="note">
					{t('workspace.outline.workflowHint')}
				</p>
				<p className="hud-hint" id="outline-locate-hint" role="note">
					{t('workspace.outline.locateHint')}
				</p>
			</div>
		</header>
	);
}

function formatCreateLabel(
	locale: 'zh-TW' | 'en',
	action: string,
	type: NodeType,
	t: ReturnType<typeof useLocale>['t']
): string {
	const target = nodeTypeLabel(type, t);
	return locale === 'zh-TW' ? `${action}${target}` : `${action} ${target}`;
}

function formatReturnLabel(currentGraphMode: WorkspaceGraphMode, t: ReturnType<typeof useLocale>['t']): string {
	return currentGraphMode === '3d'
		? t('workspace.outline.returnTo3d')
		: t('workspace.outline.returnTo2d');
}

function nodeTypeLabel(type: NodeType, t: ReturnType<typeof useLocale>['t']): string {
	if (type === 'Human') {
		return t('human');
	}

	if (type === 'Project') {
		return t('project');
	}

	return t('occupation');
}

function formatCount(locale: 'zh-TW' | 'en', visibleCount: number, totalCount: number, label: string): string {
	return locale === 'zh-TW'
		? `${label} ${visibleCount} / ${totalCount}`
		: `${label} ${visibleCount} / ${totalCount}`;
}
